import { useCallback } from 'react';

import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
	IS_NAV_4_SPACE_TOUR_DISMISSED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-live-doc-changeboarding/entry-points/onboardingStateKeys';
import { useAccessStatus } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';

export enum STEPS {
	NAV_4_GLOBAL = 'nav4Global',
	NAV_4_SPACE = 'nav4Space',
	LIVE_DOC = 'liveDoc',
	CONVERT_TO_PAGE = 'convertToPage',
	CONTENT_WRAPPER = 'contentWrapper',
}

export const STEPS_ONBOARDING_STATE_KEYS = {
	[STEPS.NAV_4_GLOBAL]: IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
	[STEPS.NAV_4_SPACE]: IS_NAV_4_SPACE_TOUR_DISMISSED,
	[STEPS.LIVE_DOC]: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocTour,
	[STEPS.CONVERT_TO_PAGE]:
		LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedConvertToPageSpotlight,
	[STEPS.CONTENT_WRAPPER]:
		LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedContentWrapperTour,
};

export const useGetModernizeChangeboardingOnboardingState = () => {
	const { isLicensed, isGuest } = useAccessStatus();
	const accessError =
		isLicensed || isGuest
			? null
			: new Error('Only licensed or guest users can retrieve onboarding state');

	if (accessError) {
		markErrorAsHandled(accessError);
	}

	const { data, loading, error } = useGetOnboardingState([
		IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
		IS_NAV_4_SPACE_TOUR_DISMISSED,
		...Object.values(LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS),
	]);

	const {
		isNav4GlobalSpotlightDismissed,
		isNav4SpaceTourDismissed,
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasDismissedLiveDocModal,
		hasDismissedContentWrapperTour,
		hasDismissedAiButtonTour,
		hasDismissedConvertToPageSpotlight,
	} = deserializeState(data);

	return {
		isNav4GlobalSpotlightDismissed,
		isNav4SpaceTourDismissed,
		hasDismissedIntroSpotlight,
		hasDismissedLiveDocTour,
		hasDismissedLiveDocModal,
		hasDismissedContentWrapperTour,
		hasDismissedAiButtonTour,
		hasDismissedConvertToPageSpotlight,
		loading,
		error: error || accessError,
	};
};

export const useSetModernizeChangeboardingOnboardingState = () => {
	const { setOnboardingState } = useSetOnboardingState();

	const completeNav4GlobalSpotlight = useCallback(() => {
		setOnboardingState({
			key: IS_NAV_4_GLOBAL_SPOTLIGHT_DISMISSED,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeNav4SpaceTour = useCallback(() => {
		setOnboardingState({
			key: IS_NAV_4_SPACE_TOUR_DISMISSED,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeContentWrapperTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedContentWrapperTour,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeLiveDocModal = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocModal,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeLiveDocTour = useCallback(() => {
		setOnboardingState({
			key: LIVE_DOC_CHANGEBOARDING_ONBOARDING_STATE_KEYS.hasDismissedLiveDocTour,
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeConvertToPageSpotlight = useCallback(() => {
		setOnboardingState({
			key: STEPS_ONBOARDING_STATE_KEYS[STEPS.CONVERT_TO_PAGE],
			value: new Date().toISOString(),
		});
	}, [setOnboardingState]);

	const completeSegments = useCallback(
		(steps: STEPS[]) => {
			if (steps.length === 0) {
				return;
			}

			setOnboardingState(
				steps.map((step) => ({
					key: STEPS_ONBOARDING_STATE_KEYS[step],
					value: new Date().toISOString(),
				})),
			);
		},
		[setOnboardingState],
	);

	return {
		completeNav4GlobalSpotlight,
		completeNav4SpaceTour,
		completeLiveDocModal,
		completeLiveDocTour,
		completeContentWrapperTour,
		completeConvertToPageSpotlight,
		completeSegments,
	};
};
